import { DashboardFolderT, DashboardT } from 'business/definitions';
import { useMemo } from 'react';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import { getDashboardsTableColDefs, isDashboardType } from './helpers';
import { useTranslation } from 'react-i18next';
import AccessCountCellRenderer from './AccessCountCellRenderer';
import { Box, styled, useTheme } from '@mui/system';
import ActionCellRenderer from './ActionCellRenderer';
import { useNavigate } from 'react-router-dom';
import { CellClickedEvent } from 'ag-grid-enterprise';
import { Breadcrumbs, Button, Typography } from '@mui/material';

import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DashboardFolderNameCellRenderer from './DashboardFolderNameCellRenderer';

type Props = {
    dashboards: DashboardT[];
    isSearching?: boolean;
    folders: DashboardFolderT[];
    handleFolder: (folder: DashboardFolderT | null) => void;
    currentFolder: DashboardFolderT | null;
};

const StyAgContainer = styled(Box, { name: 'StyAgContainer' })(({ theme }) => ({
    border: '1px solid lightgray',
    margin: theme.spacing(2),
    width: 'auto',
    height: '65vh',
    '.ag-header-row': { backgroundColor: theme.palette.lightGrey },
    '.ag-header-cell': {
        borderRight: '1px solid lightgray',
        textAlign: 'center',
    },
    '.ag-header-cell-label': {
        justifyContent: 'center',
    },
    '.ag-header-cell-text': {
        textTransform: 'unset !important',
        color: theme.palette.darkGrey,
    },
}));

const DashboardsTable = ({
    dashboards,
    isSearching = false,
    folders,
    handleFolder,
    currentFolder,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const colDefs = useMemo(
        () => getDashboardsTableColDefs(isSearching, t),
        [isSearching],
    );

    const handleCellClick = (event: CellClickedEvent) => {
        if (event.colDef?.field !== 'action') {
            if (isDashboardType(event.data)) {
                const url = `/dashboard/${event.data.slug}`;
                const mouseEvent = event.event as MouseEvent;
                if (mouseEvent?.ctrlKey || mouseEvent?.metaKey) {
                    window.open(url, '_blank');
                } else {
                    navigate(url);
                }
            } else {
                handleFolder(event.data);
            }
        }
    };

    return (
        <>
            {currentFolder && handleFolder && (
                <Breadcrumbs
                    sx={{ margin: theme.spacing(2) }}
                    separator={<NavigateNextIcon sx={{ color: 'black' }} />}
                >
                    <Button onClick={() => handleFolder(null)}>
                        <Typography variant="h4" color="primary">
                            {t('dashboards.breadcrumbsRoot')}
                        </Typography>
                    </Button>
                    <Typography variant="h4" sx={{ color: 'black' }}>
                        {currentFolder?.name}
                    </Typography>
                </Breadcrumbs>
            )}
            <StyAgContainer
                className="ag-theme-material"
                data-testid="dashboards-table"
            >
                <AgGridReact
                    rowHeight={40}
                    headerHeight={50}
                    defaultColDef={{
                        valueFormatter: ({ value }) => value ?? '-',
                        cellStyle: {
                            cursor: 'pointer',
                            color: theme.palette.darkGrey,
                            alignContent: 'center',
                        },
                    }}
                    columnDefs={colDefs}
                    rowData={
                        currentFolder || isSearching
                            ? dashboards
                            : [...folders, ...dashboards]
                    }
                    components={{
                        accessCountCellRenderer: AccessCountCellRenderer,
                        actionCellRenderer: ActionCellRenderer,
                        dashboardFolderNameCellRenderer:
                            DashboardFolderNameCellRenderer,
                    }}
                    onCellClicked={handleCellClick}
                    overlayNoRowsTemplate={t('dashboards.table.noRows')}
                    getContextMenuItems={(params) => {
                        const dashboard = params.node?.data;
                        if (isDashboardType(dashboard)) {
                            return [
                                {
                                    name: t(
                                        'dashboards.table.openDashboardInNewTab',
                                    ),
                                    action: () =>
                                        window.open(
                                            `/dashboard/${dashboard.slug}`,
                                            '_blank',
                                        ),
                                },
                                'copy',
                            ];
                        }
                        return ['copy'];
                    }}
                />
            </StyAgContainer>
        </>
    );
};

export default DashboardsTable;
